import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { IoClose } from 'react-icons/io5';
import { BiSolidMessageSquareDetail } from "react-icons/bi";
import { API_URL } from '../../config';
import { toast } from 'react-toastify';

const SubmissionForm = ({onSent}) => {
  const { phone: initialPhoneNumber } = useParams();
  const [submissionFormState, setSubmissionFormState] = useState({ showForm: false });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [preview, setPreview] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [templates, setTemplates] = useState([]);
  const [previewText, setPreviewText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const submissionFormRef = useRef();

  useEffect(() => {
    setPhoneNumber(initialPhoneNumber);
  }, [initialPhoneNumber]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API_URL}getTemplates`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
        });
        setTemplates(response.data.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      setPreview(generatePreview(selectedTemplate, variableValues));
      setPreviewText(generatePreviewText(selectedTemplate, variableValues));
    }
  }, [selectedTemplate, variableValues]);

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  const handleClickOutside = event => {
    if (submissionFormRef.current && !submissionFormRef.current.contains(event.target)) {
      setSubmissionFormState(prevState => ({ ...prevState, showForm: false }));
    }
  };

  const handleEscapeKey = event => {
    if (event.key === 'Escape') {
      setSubmissionFormState(prevState => ({ ...prevState, showForm: false }));
    }
  };

  useEffect(() => {
    if (submissionFormState.showForm) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [submissionFormState.showForm]);

  const handleTemplateChange = (templateId) => {
    const template = templates.find((template) => template.id === templateId);
    if (template && (!template.components || !Array.isArray(template.components))) {
      toast.error('Invalid template structure');
      return;
    }
    
    setSelectedTemplate(template);
    setVariableValues({});
  };

  const handlePaperclipClick = () => {
    setSubmissionFormState(prevState => ({ ...prevState, showForm: true }));
  };

  const handleCancelClick = () => {
    setSubmissionFormState(prevState => ({ ...prevState, showForm: false }));
  };

  const handleVariableChange = (variableName, value) => {
    setVariableValues(prevState => ({
      ...prevState,
      [variableName]: value
    }));
  };

  const handleSend = async () => {
    if (isLoading) {
      toast.warn('Please wait, message is already being sent');
      return;
    }

    const templateName = selectedTemplate.name;
    const templateLanguage = selectedTemplate.language;

    const headerParameters = extractParameters("HEADER", selectedTemplate, variableValues);
    const bodyParameters = extractParameters("BODY", selectedTemplate, variableValues);

    const postData = {
      messaging_product: "whatsapp",
      recipient_type: "individual",
      to: phoneNumber,
      type: "template",
      template: {
        name: templateName,
        language: {
          code: templateLanguage,
        },
        components: [
          {
            type: "header",
            parameters: headerParameters,
          },
          {
            type: "body",
            parameters: bodyParameters,
          },
        ],
      },
    };

    try {
      setIsLoading(true);      
      await axios.post(`${API_URL}sendWhatsapptemplate`, {
        postData: postData,
        message: previewText
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("token")}`
        }
      });
        await onSent?.();
        handleCancelClick();
        toast.success('Message sent successfully!');
      } 
      catch (error) {
        console.error('Error sending message:', error);
        let errorMessage = 'Something went wrong';
        if (error.response) {
          console.error('Backend Error Response:', error.response.data);
          errorMessage = error.response.data.error || 'Unknown error';
        } else if (error.request) {
          console.error('No response received:', error.request);
          errorMessage = 'No response from server';
        } else {
          console.error('Request setup error:', error.message);
          errorMessage = 'Request failed: ' + error.message;
        }
        toast.error(errorMessage, 'Error');
      } finally {
        setIsLoading(false);
      }
    };

  const extractVariables = (text) => {
    if (!text || typeof text !== 'string') {
      return [];
    }
    const regex = /{{(.*?)}}/g;
    const matches = text.match(regex);
    return matches ? matches.map((match) => match.replace(/{{|}}/g, '')) : [];
  };

  const extractParameters = (type, template, values) => {
    if (!template || !template.components) {
      return [];
    }

    return template.components
      .filter((component) => component && component.type === type && component.text)
      .flatMap((component) =>
        extractVariables(component.text).map((variable) => ({
          type: "text",
          text: values[component.type + variable] || "",
        }))
      );
  };

  const generatePreview = (template, values) => {
    if (!template || !template.components) {
      return <div>No template data available</div>;
    }

    return template.components.map((component, index) => {
      if (!component || !component.type) {
        return null;
      }
      if (component.type === 'HEADER' || component.type === 'BODY' || component.type === 'FOOTER') {
        if (!component.text) {
          return <p key={`${component.type}-${index}`}>⚠️ {component.type} component missing text</p>;
        }
        
        let text = component.text;
        for (let key in values) {
          key = key.replace("HEADER", "").replace("BODY", "").replace("FOOTER", "");
          text = text.replace(`{{${key}}}`, values[component.type + key] || `{{${key}}}`);
        }
        return <p key={`${component.type}-${index}`}>{text}</p>;
      }
      return null;
    }).filter(Boolean);
  };

  const generatePreviewText = (template, values) => {
    if (!template || !template.components) {
      return '';
    }

    return template.components
      .filter((component) => component && component.type && component.text)
      .map((component) => {
        let text = component.text;
        for (let key in values) {
          key = key.replace("HEADER", "").replace("BODY", "").replace("FOOTER", "");
          text = text.replace(`{{${key}}}`, values[component.type + key] || `{{${key}}}`);
        }
        return text;
      })
      .join('\n');
  };

  return (
    <>
      {submissionFormState.showForm && <div className="overlay"></div>}
      <div ref={submissionFormRef}>
        <BiSolidMessageSquareDetail className="icon submit-form-icon" onClick={handlePaperclipClick} />
        {submissionFormState.showForm && (
          <div className="submission-form">
            <button className="x-button" onClick={handleCancelClick}><IoClose /></button>
            <div className='center-text'>ارسال نموذج</div>
            <div>
              <label>رقم المستقبل: </label>
              <input
                type="text"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
              />
            </div>
            <select onChange={(e) => handleTemplateChange(e.target.value)}>
              <option value="">اختر نموذج</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>

            {selectedTemplate && (
              <>
                <h4>بيانات النموذج المتغيرة</h4>
                {selectedTemplate.components
                  .filter((component) => component && component.type && component.type !== 'FOOTER' && component.text)
                  .map((component) => {
                    const variables = extractVariables(component.text);
                    return (
                      <div key={component.type}>
                        <label>{component.type}</label>
                        {variables.map((variable) => (
                          <div key={variable}>
                            <label>{variable}</label>
                            <input
                              type="text"
                              value={variableValues[component.type + variable] || ''}
                              onChange={(e) => handleVariableChange(component.type + variable, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })}
                <h4>معاينة الرسالة</h4>
                <div>{preview}</div>
              </>
            )}
            <button className='primary-button' onClick={handleSend} disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="loading-spinner"></span>
                  جاري الإرسال...
                </>
              ) : (
                'ارسال'
              )}
            </button>
            <button className='primary-outline-button' onClick={handleCancelClick} disabled={isLoading}>الغاء</button>
          </div>
        )}
      </div>
    </>
  );
};

export default SubmissionForm;
